// @ts-check // Enable script type checking for this file
// instead of being defined explicitly
import {initializeApp, getApps} from 'firebase/app';
import {connectAuthEmulator, getAuth, OAuthProvider, signInWithPopup} from 'firebase/auth';
import {
  connectFirestoreEmulator, getFirestore, getDoc, collection, collectionGroup, doc, getDocs, documentId,
  where as whereConstraint, query, orderBy as orderConstraint, startAt, endAt, limit as limitConstraint,
  updateDoc, setDoc, addDoc, deleteField, deleteDoc, onSnapshot, getCountFromServer, writeBatch, runTransaction,
} from 'firebase/firestore';
import {CustomProvider, initializeAppCheck, ReCaptchaEnterpriseProvider} from 'firebase/app-check';
import {connectStorageEmulator, getStorage, ref, uploadString, getBlob, deleteObject} from 'firebase/storage';
import {getRemoteConfig, fetchAndActivate, activate, getValue, setCustomSignals, onConfigUpdate} from 'firebase/remote-config';
export {Timestamp, GeoPoint, FieldValue, increment, deleteField} from 'firebase/firestore';
export {onAuthStateChanged} from 'firebase/auth';
import {converter} from './converter';
import {wrapError} from '@northpoint-development/labs-common-core';
/**
 * @typedef {import('firebase/app').FirebaseApp} FirebaseApp
 * @typedef {import('firebase/auth').Auth} Auth
 * @typedef {import('firebase/auth').User} User
 * @typedef {import('firebase/auth').CustomParameters} CustomParameters
 * @typedef {import('firebase/firestore').Firestore} Firestore
 * @typedef {import('firebase/firestore').DocumentReference} DocumentReference
 * @typedef {import('firebase/firestore').CollectionReference} CollectionReference
 * @typedef {import('firebase/firestore').QuerySnapshot} QuerySnapshot
 * @typedef {import('firebase/firestore').DocumentData} DocumentData
 * @typedef {import('firebase/firestore').Query} Query
 * @typedef {import('firebase/firestore').QueryConstraint} QueryConstraint
 * @typedef {import('firebase/firestore').FieldPath} FieldPath
 * @typedef {import('firebase/firestore').WhereFilterOp} WhereFilterOp
 * @typedef {import('firebase/storage').FirebaseStorage} Storage
 * @typedef {import('firebase/app-check').AppCheck} AppCheck
 * @typedef {import('firebase/remote-config').RemoteConfig} RemoteConfig
 * @typedef {import('firebase/firestore').FirestoreDataConverter<any, DocumentData>} FirestoreDataConverter
 */

/** @type {FirebaseApp} */
let app;
/** @type {Auth} */
let auth;
/** @type {Firestore} */
let db;
/** @type {Storage} */
let storage;
/** @type {AppCheck | null} */
let appCheck;
/** @type {RemoteConfig} */
let remoteConfig;

/**
 * @typedef {import('./types').FirebaseConfig} FirebaseConfig
 */

/** Firestore errors that should prompt an upload to Firebase Storage. */
const ATTEMPT_STORAGE_ERRORS = ['too many index entries', 'exceeds the maximum allowed size'];

/**
 * This must be called exactly once before any other Firestore functions are called.
 * @param {FirebaseConfig} config
 */
const initializeFirebase = (config) => {
  // Override config with PLAYWRIGHT_FIREBASE_CONFIG if it exists
  const playwrightFirebaseConfig = window['PLAYWRIGHT_FIREBASE_CONFIG'];
  if (playwrightFirebaseConfig?.firebaseInit) {
    console.log('PLAYWRIGHT_FIREBASE_CONFIG firebase:', {
      projectId: playwrightFirebaseConfig.firebaseInit?.projectId,
      databaseId: playwrightFirebaseConfig.firebaseInit?.databaseId,
    });
    config = playwrightFirebaseConfig.firebaseInit;
    config.emulators = playwrightFirebaseConfig.emulators;
  }

  const existingApp = getApps().find((a) => a.name === config.name);
  app = existingApp ?? initializeApp(config, config.name);

  auth = getAuth(app);
  db = getFirestore(app, config?.databaseId);
  storage = getStorage(app);
  appCheck = getAppCheck(app);

  if (process.env.VUE_APP_FIREBASE_ENV === 'development') {
    window['firebase'] = /** @type {never} */ ({app, auth, db, storage, appCheck});
  }

  // Connect to emulators if they are defined in PLAYWRIGHT_FIREBASE_CONFIG
  if (config.emulators) {
    const emulators = config.emulators;
    connectFirestoreEmulator(db, emulators.firestore.host, emulators.firestore.port, emulators.firestore.options);
    connectAuthEmulator(auth, emulators.auth.url, emulators.auth.options);
    connectStorageEmulator(storage, emulators.storage.host, emulators.storage.port, emulators.storage.options);
    // @ts-ignore We can smooth out types later
    remoteConfig = emulators.remoteConfig;
  } else {
    remoteConfig = getRemoteConfig(app);
  }
};

/**
 * @param {FirebaseApp} app
 * @return {AppCheck | null}
 */
const getAppCheck = (app) => {
  if (!process.env.VUE_APP_RECAPTCHA_SITE_KEY) return null;
  // If PLAYWRIGHT_FIREBASE_CONFIG has emulators, we are running Playwright tests and need to use a fake token
  if (typeof window !== 'undefined' && window['PLAYWRIGHT_FIREBASE_CONFIG']?.emulators) {
    return initializeAppCheck(app, {
      provider: new CustomProvider({
        getToken: () => {
          return Promise.resolve({
            token: 'fake-token',
            expireTimeMillis: Date.now() + 1000 * 60 * 60 * 24, // 24 hours
          });
        },
      }),
      isTokenAutoRefreshEnabled: true,
    });
  }
  return initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(process.env.VUE_APP_RECAPTCHA_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
};

/**
 * @param {string} providerId
 * @param {CustomParameters} [customParams]
 */
const oAuthSignIn = async (providerId, customParams) => {
  const provider = new OAuthProvider(providerId);
  if (customParams) {
    provider.setCustomParameters(customParams);
  }

  /*
    Using signInWithRedirect() breaks because our origin and auth redirect domains are different and
    'third-party cookies' are being blocked by the browser, despite not REALLY being third-party in this case.

    This could be fixed by switching to Firebase hosting, but we are not committing to that yet.
    We will need to look more into Firebase hosting before making a decision.

    If using a mobile device with signInWithPopup(), you will need to enable popups in your browser.
  */

  // if (isMobile()) {
  //   return await signInWithRedirect(auth, provider);
  // }

  return await signInWithPopup(auth, provider);
};

const currentUser = () => auth.currentUser;

/**
 * Retrieves a collection from Firestore in the form of a snapshot of its child documents.
 * @param {string} collectionName The Firestore collection name.
 */
const getCollection = (collectionName) =>
  getDocs(getCollectionRef(collectionName));

/**
 * Retrieves a document from Firestore in the form of a JavaScript object.
 * @param {string} collection The Firestore collection name.
 * @param {string} document The ID of the document to retrieve.
 * @param {boolean} includeIds Whether to embed the IDs in the returned document.
 */
const getDocument = (collection, document, includeIds = false) =>
  getDoc(getDocRef(collection, document)).then((snapshot) => {
    const data = snapshot.data();
    if (includeIds && data) {
      // Make sure that the snapshot ID field overrides any existing 'id' field in the document data to prevent embedded
      // ID fields from being treated as canonical document IDs.
      return {...data, id: snapshot.id};
    }
    return data;
  });

/**
 * Retrieves a document from Firestore and returns the snapshot.
 * @param {string} collection The Firestore collection name.
 * @param {string} document The ID of the document to retrieve.
 */
const documentExists = async (collection, document) =>
  (await getDoc(getDocRef(collection, document))).exists();


/**
 * Retrieve documents from a Firestore collection by a list of IDs.
 * @param {string} collection The Firestore collection name.
 * @param {string[]} ids The IDs of the documents to retrieve.
 * @param {boolean} includeIds Whether to embed the IDs in the returned documents.
 * @returns {Promise<(DocumentData & {id: string})[]>}
 */
const queryCollectionByIds = async (collection, ids, includeIds) => {
  const promises = [];
  // the "in" clause can only accept a maximum of 10 IDs, so we need to split the list into chunks
  while (ids.length > 0) {
    promises.push(queryCollection(
      collection,
      {
        where: [{field: documentId(), opStr: 'in', value: ids.splice(0, 10)}],
      },
      includeIds,
    ));
  }
  return (await Promise.all(promises)).flat();
};

/**
 * Retrieves a single field from a Firestore document.
 * @param {string} collection The Firestore collection name.
 * @param {string} document The ID of the document to retrieve.
 * @param {string} field The name of the field to retrieve.
 */
const getDocumentField = (collection, document, field) =>
  getDoc(getDocRef(collection, document)).then((snapshot) => snapshot.data()?.[field]);

/**
 * Retrieve all documents from a Firestore collection where a field's operation on a value returns true.
 * @param {string} collection The Firestore collection name.
 * @param {string} field The name of the field to check against.
 * @param {*} value The value of `field` being sought.
 * @param {WhereFilterOp} opStr String representing the operation (per `DocumentData.where`)
 */
const queryCollectionByValue = (collection, field, value, opStr = '==') =>
  queryCollection(collection, {where: [{field, value, opStr}]});

/**
 * Retrieve all documents from a Firestore collection where a field has values within a specified range.
 * @param {string} collection The Firestore collection name.
 * @param {string} field The name of the field to check against.
 * @param {*} start The lowest acceptable value.
 * @param {*} end The highest acceptable value.
 */
const queryCollectionByRange = (collection, field, start, end) =>
  queryCollection(collection, {orderBy: [{field, order: 'asc'}], range: {start, end}});

/**
 * @typedef {Object} QueryOptions
 * @property {{field: string | FieldPath, value: *, opStr: import('firebase/firestore').WhereFilterOp}[]} [where]
 * An array of objects with `field` and `value` properties.
 * @property {{field: string, order: import('firebase/firestore').OrderByDirection}[]} [orderBy]
 * An object with `field` and `order` properties.
 * @property {{start: *, end: *}} [range]
 * An object with `start` and `end` properties. Must be defined in conjunction with `orderBy`.
 * @property {number} [limit]
 * The max number of documents to return. Required in conjunction with `orderBy`; may not be combined with `range`.
 * @property {boolean} [isSubcollection]
 * Whether the collection is a subcollection of another collection.
 * @property {boolean} [resolveParentId]
 * Whether to resolve the parent ID of the document. Only applicable if `isSubcollection` is true.
 */

/**
 * Query to retrieve all documents from a Firestore collection, optionally restricted by where clauses and a range.
 * [Firestore Queries]{@link https://firebase.google.com/docs/firestore/query-data/order-limit-data}
 * @param {string} collection The Firestore collection name.
 * @param {QueryOptions} options An object with various query options.
 * @param {boolean} [includeIds=false] Whether to embed the ID of each document in the returned data.
 * @throws {Error} If an invalid combination of parameters is provided.
 */
const queryCollection = async (
  collection,
  {where = [], orderBy = [], range = undefined, limit = 0, isSubcollection = false, resolveParentId = false} = {},
  includeIds = false,
) => {
  /** @type {QueryConstraint[]} */
  const constraints = [];
  where.forEach((whereClause) => {
    constraints.push(whereConstraint(whereClause.field, whereClause.opStr || '==', whereClause.value));
  });
  orderBy.forEach((orderClause, i) => {
    // for the first order clause
    if (i === 0) {
      // check if any where clause uses a range comparison (anything but '==')
      const rangeWhere = where.find((whereClause) => whereClause.opStr !== '==');
      // Per Firestore docs: if there is a range comparison and this order by is
      // NOT for the same field as the range, throw an error
      if (rangeWhere && orderClause.field !== rangeWhere.field) {
        throw new Error(`First orderBy clause for ${orderClause.field} must be the same as the where clause for ${rangeWhere.field} using a range comparison`);
      }
    }
    constraints.push(orderConstraint(orderClause.field, orderClause.order || 'asc'));
  });
  if (range) {
    if (!orderBy.length) {
      throw new Error('Must define at least one orderBy when defining range');
    }
    constraints.push(startAt(range.start));
    constraints.push(endAt(range.end));
  }
  if (limit > 0) {
    if (!orderBy.length) {
      throw new Error('Must define orderBy when defining limit');
    }
    if (range) {
      throw new Error('Must not define both range and limit');
    }
    constraints.push(limitConstraint(limit));
  }
  const refFn = isSubcollection ? getSubCollectionRef : getCollectionRef;
  const q = query(refFn(collection), ...constraints).withConverter(converter);
  try {
    const snapshot = await getDocs(q);
    return getDataFromQuerySnapshot(snapshot, includeIds, isSubcollection && resolveParentId);
  } catch (error) {
    throw new Error('Error getting data from query' + error);
  }
};

/**
 * Updates a specified document in Firestore with the provided metadata.
 * @param {string} collection The Firestore collection name.
 * @param {string} document The ID of the document to update.
 * @param {Object} metadata Object of names and values to write
 */
const updateDocument = async (collection, document, metadata) => {
  const docRef = getDocRef(collection, document);
  // withConverter() is only called on a document reference when it is created or set
  // so we manually call the toFirestore() method on the metadata object to ensure it is converted
  // when the document is partially updated
  if (!docRef.converter) throw new Error('Converter not set for document reference');
  metadata = docRef.converter.toFirestore(metadata);

  const incompatiblePropertyTypes = ['function'];
  const checkProperties = (/** @type {any} */ obj) => {
    for (const key in obj) {
      if (obj[key] === null) continue;

      const propertyType = typeof obj[key];
      if (propertyType === 'object') {
        checkProperties(obj[key]);
      }

      if (incompatiblePropertyTypes.includes(propertyType)) {
        console.warn(`Property "${key}" is of type "${propertyType}" and will be removed before saving.`);
        delete obj[key];
      }
    }
  };

  checkProperties(metadata);
  const updateFn = await getUpdateFn(docRef);
  return await updateFn(docRef, metadata);
};

/**
 * Add a new document to a Firestore collection.
 * @param {string} collection The Firestore collection name.
 * @param {Object} metadata Object of names and values to write
 * @param {FirestoreDataConverter} [firestoreConverter=converter] Optional, a Firestore data converter to use
 * for the document.
 */
const addDocument = async (collection, metadata, firestoreConverter = converter) => {
  return (await addDoc(getCollectionRef(collection, firestoreConverter), metadata)).id;
};

/**
 * Writes a document to Firestore with a specific ID. Overwrites any existing document at that path.
 * Idempotent — safe for retries and backfills.
 * @param {string} collection The name of the collection.
 * @param {string} documentId The specific ID to write to.
 * @param {Object} metadata Object of names and values to write.
 * @param {FirestoreDataConverter} [firestoreConverter=converter] Optional converter.
 */
const setDocument = async (collection, documentId, metadata, firestoreConverter = converter) => {
  await setDoc(getDocRef(collection, documentId).withConverter(firestoreConverter), metadata);
};

/**
 * @typedef {Object} BatchWriteOp
 * @property {'set' | 'update'} op The Firestore write operation to perform.
 * @property {string} collection The Firestore collection name.
 * @property {string} docId The ID of the document to write.
 * @property {Object} data Object of names and values to write.
 */

/**
 * Commits multiple writes atomically as a single Firestore batched write. Either all writes succeed or none of them do.
 * Uses a raw `doc(...)` ref (no `withConverter`) because the Firestore SDK auto-applies an attached converter on `set`
 * but not on `update`. Manual conversion + raw ref keeps the behavior identical for both ops.
 * @param {BatchWriteOp[]} ops
 */
const runBatchedWrites = async (ops) => {
  if (!ops.length) return;
  if (ops.length > 500) {
    throw new Error(`runBatchedWrites: ${ops.length} ops exceeds Firestore's 500-operation batch limit`);
  }
  const batch = writeBatch(db);
  for (const {op, collection, docId, data} of ops) {
    const docRef = doc(db, collection, docId);
    const convertedData = converter.toFirestore(data);
    if (op === 'set') {
      batch.set(docRef, convertedData);
    } else if (op === 'update') {
      batch.update(docRef, convertedData);
    } else {
      throw new Error(`runBatchedWrites: unsupported op "${op}"`);
    }
  }
  await batch.commit();
};

/**
 * @typedef {Object} TransactionHandle
 * @property {(collection: string, docId: string) => Promise<DocumentData | null>} get Reads a document with its `id`;
 * every read must come before the first write.
 * @property {(collection: string, docId: string, data: Object) => void} set Queues a full overwrite.
 * @property {(collection: string, docId: string, data: Object) => void} update Queues a partial update; the document
 * must exist.
 */

/**
 * Firestore re-runs `updateFn` when a document it read changes before the commit, so it must only read and queue
 * writes. Writes convert manually, as in {@link runBatchedWrites}.
 * @template T
 * @param {(transaction: TransactionHandle) => Promise<T>} updateFn
 * @returns {Promise<T>}
 */
const runInTransaction = (updateFn) => runTransaction(db, (transaction) => updateFn({
  get: async (collection, docId) => {
    const snapshot = await transaction.get(getDocRef(collection, docId));
    return snapshot.exists() ? {...snapshot.data(), id: snapshot.id} : null;
  },
  set: (collection, docId, data) => {
    transaction.set(doc(db, collection, docId), converter.toFirestore(data));
  },
  update: (collection, docId, data) => {
    transaction.update(doc(db, collection, docId), converter.toFirestore(data));
  },
}));

/**
 * Duplicates a document in Firestore. If specified, subcollections within the document will also be duplicated.
 * @param {string} collection The collection in which the document exists.
 * @param {string} document The ID of the document to duplicate.
 * @param {string[]} subcollections Optional, a list of subcollections in the target document to duplicate.
 * @return {Promise<string>} The ID of the new document.
 */
const duplicateDocument = async (collection, document, subcollections = []) => {
  try {
    const docRef = getDocRef(collection, document);
    const snapshot = await getDoc(docRef);
    const data = snapshot.data();
    const newDocId = await addDocument(collection, data);

    if (subcollections.length) {
      for (const subcollection of subcollections) {
        const subcollectionDocs = await getSubcollection(collection, document, subcollection);
        for (const doc of subcollectionDocs) {
          await addSubcollectionDocument(collection, newDocId, subcollection, doc);
        }
      }
    }

    return newDocId;
  } catch (error) {
    throw wrapError(error, `Could not duplicate document`);
  }
};

/**
 * Delete a document in Firestore.
 * @param {string} collection The Firestore collection name.
 * @param {string} document The ID of the document to delete.
 * @param {Array<string>} [subcollections] An optional array of any subcollections to delete belonging to the document.
 * @param {boolean} [softDelete] Whether to mark the document as deleted instead of deleting it.
 */
const deleteDocument = async (collection, document, subcollections = [], softDelete = false) => {
  // Iterate through all root-level subcollections within the document to be deleted and remove that data
  if (softDelete) {
    const now = new Date();
    const user = currentUser();
    return await updateDocument(collection, document, {
      deleted: now,
      editedOn: {
        name: user?.displayName ?? '',
        timestamp: now,
        uid: user?.uid ?? null,
      },
    });
  } else {
    if (subcollections.length) {
      for (let i = 0; i < subcollections.length; i++) {
        const subcollection = subcollections[i];
        const subcollectionDocs = await getSubcollection(collection, document, subcollection, true);
        if (subcollectionDocs.length) {
          for (let i = 0; i < subcollectionDocs.length; i++) {
            // Must await deleting subcollections to prevent Firestore from blowing up (since it doesn't
            // like deleting documents before subcollections)
            await deleteSubcollectionDocument(collection, document, subcollection, subcollectionDocs[i].id);
          }
        }
      }
    }
    return await deleteDoc(getDocRef(collection, document));
  }
};

/**
 * Update the value of a Firestore document in the subcollection of a parent document, adding the field if it is not
 * already present.
 * @param {string} collection The Firestore collection name.
 * @param {string} document The ID of the parent document.
 * @param {string} subcollection The name of the subcollection to update.
 * @param {string} subdocument The ID of the subdocument to update.
 * @param {Object} metadata Object of names and values to write
 */
const updateSubcollectionDocument = async (collection, document, subcollection, subdocument, metadata) => {
  const subDocRef = doc(db, collection, document, subcollection, subdocument);
  const documentData = (await getDoc(subDocRef)).data();

  // We already have a firebaseStorageLink, so we need to update the object in storage
  if (documentData?.firebaseStorageLink) {
    const storage = getStorage(app);
    const path = documentData.firebaseStorageLink;
    const storageRef = ref(storage, path);
    const obj = await getFromFirebaseStorage(storageRef);
    const update = Object.assign(obj, metadata);
    await uploadToFirebaseStorage(storageRef, JSON.stringify(update));
    return;
  }

  try {
    const updateFn = await getUpdateFn(subDocRef);
    await updateFn(subDocRef, metadata);
  } catch (error) {
    if (ATTEMPT_STORAGE_ERRORS.some((err) => error.message.includes(err))) {
      const storage = getStorage(app);
      const path = `firestore-heavy-documents/${collection}/${document}/${subcollection}/${subdocument}`;
      const storageRef = ref(storage, path);
      await uploadToFirebaseStorage(storageRef, JSON.stringify(metadata));
      await updateSubcollectionDocument(
        collection, document, subcollection, subdocument, {firebaseStorageLink: path},
      );
    }
    throw wrapError(error, `Could not update subcollection document for ${collection}/${document}/${subcollection}/${subdocument}`);
  }
};

/**
 * Add a new document to the subcollection of a parent document and return the ID of the new document.
 * @param {string} collectionName The Firestore collection name.
 * @param {string} document The ID of the parent document.
 * @param {string} subcollection The name of the subcollection to update.
 * @param {Object} metadata Object of names and values to write
 * @param {string} [assignId] The ID to assign to the new document. If not provided, Firestore will generate one.
 */
const addSubcollectionDocument = async (collectionName, document, subcollection, metadata, assignId = undefined) => {
  try {
    let subcollectionRef;
    if (assignId) {
      const collectionRef = collection(db, collectionName, document, subcollection).withConverter(converter);
      subcollectionRef = doc(collectionRef, assignId);
      await setDoc(subcollectionRef, metadata);
    } else {
      subcollectionRef = await addDoc(
        collection(db, collectionName, document, subcollection).withConverter(converter), metadata,
      );
    }
    return subcollectionRef.id;
  } catch (error) {
    if (ATTEMPT_STORAGE_ERRORS.some((err) => error.message.includes(err))) {
      let subcollectionRef;
      if (assignId) {
        const collectionRef = collection(db, collectionName, document, subcollection).withConverter(converter);
        subcollectionRef = doc(collectionRef, assignId);
        const storage = getStorage(app);
        const path = `firestore-heavy-documents/${collectionName}/${document}/${subcollection}/${assignId}`;
        const storageRef = ref(storage, path);
        await uploadToFirebaseStorage(storageRef, JSON.stringify(metadata));
        await setDoc(subcollectionRef, {firebaseStorageLink: path});
      } else {
        // create document in firestore first to get document id
        const collectionRef = collection(db, collectionName, document, subcollection).withConverter(converter);
        subcollectionRef = await addDoc(collectionRef, {firebaseStorageLink: ''});
        const storage = getStorage(app);
        const path = `firestore-heavy-documents/${collectionName}/${document}/${subcollection}/${subcollectionRef.id}`;
        const storageRef = ref(storage, path);
        await uploadToFirebaseStorage(storageRef, JSON.stringify(metadata));
        await updateSubcollectionDocument(
          collectionName, document, subcollection, subcollectionRef.id, {firebaseStorageLink: path},
        );
      }
      return subcollectionRef.id;
    }
    throw wrapError(error, `Could not add subcollection document for ${collectionName}/${document}/${subcollection}`);
  }
};

/**
 * Uploads an object to Firebase Storage.
 * @param {import('firebase/storage').StorageReference} storageRef
 * @param {string} content
 */
const uploadToFirebaseStorage = async (storageRef, content) => {
  return uploadString(storageRef, content);
};

/**
 * Deletes an object from Firebase Storage.
 * @param {import('firebase/storage').StorageReference} storageRef
 */
const deleteFromFirebaseStorage = async (storageRef) => {
  return deleteObject(storageRef);
};

/**
 * Retrieves an object from Firebase Storage.
 * @param {import('firebase/storage').StorageReference} storageRef
 * @return {Promise<Object>} The object stored in Firebase Storage.
 */
const getFromFirebaseStorage = async (storageRef) => {
  // getBlob() is only available in the browser - use something like getStream() in Node
  const blob = await getBlob(storageRef);
  const obj = JSON.parse(await blob.text());
  return obj;
};

/**
 * Deletes a document from the subcollection of a parent document.
 * @param {string} collection The Firestore collection name.
 * @param {string} document The ID of the parent document.
 * @param {string} subcollection The name of the subcollection to update.
 * @param {string} subdocument The ID of the subdocument to update.
 */
const deleteSubcollectionDocument = async (collection, document, subcollection, subdocument) => {
  const documentToDelete = doc(db, collection, document, subcollection, subdocument);
  const documentData = (await getDoc(documentToDelete)).data();
  if (documentData?.['firebaseStorageLink']) {
    const storage = getStorage(app);
    const path = documentData['firebaseStorageLink'];
    const storageRef = ref(storage, path);
    await deleteFromFirebaseStorage(storageRef);
  }

  return deleteDoc(doc(db, collection, document, subcollection, subdocument));
};

/**
 * Return a list of documents in a subcollection of a parent document.
 * @param {string} collectionName The parent Firestore collection name.
 * @param {string} document The ID of parent document.
 * @param {string} subcollection The name of the subcollection to get.
 * @param {boolean} [includeIds=false] Whether to embed the ID of each document in the returned data.
 * A Promise that resolves to an array of document data as objects.
 */
const getSubcollection = async (collectionName, document, subcollection, includeIds = false) => {
  const subCollectionRef = collection(db, collectionName, document, subcollection).withConverter(converter);
  const snapshot = await getDocs(subCollectionRef);
  const queryResults = getDataFromQuerySnapshot(snapshot, includeIds);

  /** @type {DocumentData[]} */
  const data = [];

  for (let i = 0; i < queryResults.length; i++) {
    const docData = queryResults[i];
    try {
      if (docData['firebaseStorageLink']) {
        const obj = await getFromFirebaseStorage(ref(storage, docData['firebaseStorageLink']));
        if (includeIds) {
          Object.assign(obj, {id: docData.id});
        }
        data.push(obj);
      } else {
        if (includeIds) {
          Object.assign(docData, {id: docData.id});
        }
        data.push(docData);
      }
    } catch (error) {
      console.error(wrapError(error, `Could not get subcollection document for ${collectionName}/${document}/${subcollection}/${docData.id}`));
      continue;
    }
  }
  return data;
};

/**
 * Return a list of documents in a subcollection of a parent document.
 * @param {*} collectionName the parent Firestore collection name
 * @param {*} document the id of the parent document
 * @param {*} subcollection the id of the subcollection
 * @param {*} subdocument the id of the subdocument
 * @returns {Promise<DocumentData>} A Promise that resolves to the document data as an object.
 */
const getSubcollectionDocument = async (collectionName, document, subcollection, subdocument) => {
  const subDocRef = doc(db, collectionName, document, subcollection, subdocument);
  const subDoc = await getDoc(subDocRef);
  return {...subDoc.data(), id: subDocRef.id};
};

/**
 * Update a document in Firestore to remove a specified field.
 * @param {string} collection The Firestore collection name.
 * @param {string} document The ID of the document to update.
 * @param {string} field The name of the field to clear.
 */
const clearDocumentField = async (collection, document, field) => {
  const docRef = getDocRef(collection, document);
  const snapshot = await getDoc(docRef);
  const data = snapshot?.data();
  if (snapshot && data && data[field]) {
    return updateDoc(docRef, {[field]: deleteField()});
  }
};

/**
 * Listen to a Firestore collection for changes and call the provided callback function with the snapshot.
 * @param {string} collectionName The Firestore collection name.
 * @param {(snapshot: QuerySnapshot) => void} callback The callback function to call with the snapshot.
 * @returns {function(): void} A function to unsubscribe from the listener.
 */
const listenToCollection = (collectionName, callback) => {
  let unsubscribe = null;

  const collectionRef = getCollectionRef(collectionName);
  unsubscribe = onSnapshot(collectionRef, (querySnapshot) => {
    callback(querySnapshot);
  });

  return () => {
    if (unsubscribe) {
      unsubscribe();
    }
  };
};

/**
 * Like {@link listenToCollection} but restricted by simple `where` clauses (server-side filter).
 * @param {string} collectionName The Firestore collection name.
 * @param {{field: string, opStr: import('firebase/firestore').WhereFilterOp, value: *}[]} whereClauses
 * @param {(snapshot: QuerySnapshot) => void} callback The callback to call with the snapshot.
 * @returns {function(): void} A function to unsubscribe from the listener.
 */
const listenToCollectionQuery = (collectionName, whereClauses, callback) => {
  const collectionRef = getCollectionRef(collectionName);
  const constraints = (whereClauses ?? []).map((c) => whereConstraint(c.field, c.opStr ?? '==', c.value));
  const q = query(collectionRef, ...constraints);
  return onSnapshot(q, (querySnapshot) => callback(querySnapshot));
};

// Helpers
const getUpdateFn = async (/** @type {DocumentReference} */docRef) =>
  getDoc(docRef).then((snapshot) => snapshot.exists() ? updateDoc : setDoc);
const getCollectionRef = (/** @type {string} */collectionName, firestoreConverter = converter) =>
  collection(db, collectionName).withConverter(firestoreConverter);
const getSubCollectionRef = (/** @type {string} */collectionName) =>
  collectionGroup(db, collectionName).withConverter(converter);
const getDocRef = (/** @type {string} */collection, /** @type {string} */document) => {
  if (typeof document !== 'string') {
    const docType = typeof document;
    const docValue = docType === 'symbol' ? document.toString() : String(document);
    throw new TypeError(`FirestoreService.getDocRef: document ID must be a string, got ${docType}: ${docValue}`);
  }
  return doc(db, collection, document).withConverter(converter);
};

/**
 * @param {QuerySnapshot} snapshot
 * @param {boolean} [includeIds] Whether to include the document ID in the returned data.
 * @param {boolean} [resolveParentId] Whether to resolve the parent ID in the returned data.
 * @return {DocumentData[]}
 */
const getDataFromQuerySnapshot = (snapshot, includeIds, resolveParentId) => {
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    if (includeIds) {
      data.id = doc.id;
    }
    // object hierarchy is by path, so for //documents/{collection}/{document}/{subcollection}/{subdocument}, the parent
    // of {subdocument} is {subcollection}, and parent.parent is {document}
    if (resolveParentId) {
      data.parentId = doc.ref.parent.parent?.id;
    }
    return data;
  });
};

/**
 * @typedef {Object} CountQueryOptions
 * @property {Array<{field: string | FieldPath, value: any, opStr: WhereFilterOp}>} [where] Array of where clauses.
 */

/**
 * Count documents in a Firestore collection based on query options.
 * @param {string} collectionName The Firestore collection name.
 * @param {CountQueryOptions} [options={}] An object with query options, primarily `where` clauses.
 * @returns {Promise<number>} The count of documents matching the query.
 */
const countFromCollection = async (collectionName, {where = []} = {}) => {
  const currentCollection = collection(db, collectionName);
  /** @type {QueryConstraint[]} */
  const constraints = [];
  where.forEach((whereClause) => {
    constraints.push(whereConstraint(whereClause.field, whereClause.opStr || '==', whereClause.value));
  });

  const q = query(currentCollection, ...constraints);
  const snapshot = await getCountFromServer(q);
  return snapshot.data().count;
};

/**
 * Creates a local doc ref. This is done so that we can reserve an ID before actually setting the document data.
 * @param {string} collectionName The Firestore collection name.
 * @returns {DocumentReference} A reserved document reference.
 */
const createDocRef = (collectionName) => {
  return doc(collection(db, collectionName).withConverter(converter));
};

/**
 * Sets the data given a local doc ref item
 * @param {DocumentReference} docRef
 * @param {Object} data
 */
const setDocRef = async (docRef, data) => {
  docRef = docRef.withConverter(converter);
  await setDoc(docRef, data);
};

export {
  getCollection,
  getDocument,
  queryCollectionByIds,
  getDocumentField,
  getSubcollection,
  getSubcollectionDocument,
  queryCollection,
  countFromCollection,
  queryCollectionByValue,
  queryCollectionByRange,
  updateDocument,
  addDocument,
  setDocument,
  runBatchedWrites,
  runInTransaction,
  deleteDocument,
  updateSubcollectionDocument,
  addSubcollectionDocument,
  deleteSubcollectionDocument,
  clearDocumentField,
  initializeFirebase,
  currentUser,
  getDocRef,
  oAuthSignIn,
  documentExists,
  duplicateDocument,
  fetchAndActivate,
  activate,
  getValue,
  setCustomSignals,
  listenToCollection,
  listenToCollectionQuery,
  createDocRef,
  setDocRef,
  onConfigUpdate,

  // The following are exported temporarily pending API Service refactor:
  auth,
  storage,
  db,
  app,
  appCheck,
  remoteConfig,
};
